/** Race SFX: theme race_sound in a seamless loop + Web Audio crash/boost/countdown. */

import defaultRaceMp3 from "../img/race.mp3";

let audioCtx = null;
let masterGain = null;
let unlocked = false;
let engineWanted = false;
let engineGain = null;
let raceSrc = defaultRaceMp3;
let loadId = 0;
let preloadPromise = Promise.resolve();

/** 'buffer' = gapless Web Audio loop; 'dual' = overlapping HTMLAudio crossfade. */
let engineMode = "none";
let raceBuffer = null;
let bufferSource = null;

let dualA = null;
let dualB = null;
let dualActive = null;
let dualFading = false;
let engineVol = 0.9;

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  return audioCtx;
}

function ensureMaster() {
  const ctx = getCtx();
  if (!ctx) return null;
  if (!masterGain) {
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.92;
    masterGain.connect(ctx.destination);
  }
  if (!engineGain) {
    engineGain = ctx.createGain();
    engineGain.gain.value = 0.9;
    engineGain.connect(masterGain);
  }
  return masterGain;
}

function disposeBufferSource() {
  if (!bufferSource) return;
  try {
    bufferSource.onended = null;
    bufferSource.stop();
  } catch {
    /* already stopped */
  }
  try {
    bufferSource.disconnect();
  } catch {
    /* ignore */
  }
  bufferSource = null;
}

function disposeDual() {
  dualFading = false;
  [dualA, dualB].forEach((el) => {
    if (!el) return;
    try {
      el.pause();
      el.src = "";
    } catch {
      /* ignore */
    }
  });
  dualA = null;
  dualB = null;
  dualActive = null;
}

function makeDualEl(url) {
  const el = new Audio(url);
  el.preload = "auto";
  el.loop = false;
  el.playsInline = true;
  el.volume = 0;
  return el;
}

function setupDual(url) {
  disposeDual();
  dualA = makeDualEl(url);
  dualB = makeDualEl(url);
  dualActive = dualA;
  engineMode = "dual";
}

async function decodeRaceBuffer(url) {
  const ctx = getCtx();
  if (!ctx) return null;
  const res = await fetch(url, { mode: "cors", credentials: "omit", cache: "force-cache" });
  if (!res.ok) throw new Error(`race sound ${res.status}`);
  const raw = await res.arrayBuffer();
  return ctx.decodeAudioData(raw.slice(0));
}

async function preloadRaceSound(url) {
  const id = ++loadId;
  engineMode = "none";
  raceBuffer = null;
  disposeBufferSource();
  disposeDual();

  try {
    const buffer = await decodeRaceBuffer(url);
    if (id !== loadId) return;
    raceBuffer = buffer;
    engineMode = "buffer";
  } catch {
    if (id !== loadId) return;
    setupDual(url);
  }
}

/**
 * Use theme `race_sound` URL when set; otherwise built-in race.mp3.
 */
export function setEngineSoundSrc(url) {
  const next = String(url || "").trim() || defaultRaceMp3;
  if (next === raceSrc && (raceBuffer || dualA)) return;
  raceSrc = next;
  engineWanted = false;
  disposeBufferSource();
  preloadPromise = preloadRaceSound(next);
}

function startBufferLoop() {
  const ctx = getCtx();
  ensureMaster();
  if (!ctx || !raceBuffer || !engineGain) return;

  disposeBufferSource();
  const src = ctx.createBufferSource();
  src.buffer = raceBuffer;
  src.loop = true;
  src.playbackRate.value = 1;

  // Skip MP3 encoder padding so the wrap does not click / drop out.
  const dur = raceBuffer.duration;
  const pad = dur > 0.6 ? Math.min(0.045, dur * 0.015) : 0;
  if (pad > 0) {
    src.loopStart = pad;
    src.loopEnd = Math.max(pad + 0.2, dur - pad);
  }

  src.connect(engineGain);
  src.onended = () => {
    bufferSource = null;
    if (engineWanted && unlocked) startBufferLoop();
  };
  bufferSource = src;
  try {
    src.start(0);
  } catch {
    /* ignore */
  }
}

function startDualLoop() {
  if (!dualA || !dualB) setupDual(raceSrc);
  dualFading = false;
  dualActive = dualA;
  dualB.pause();
  dualB.currentTime = 0;
  dualB.volume = 0;
  dualA.volume = engineVol;
  try {
    dualA.currentTime = 0;
  } catch {
    /* ignore */
  }
  const play = dualA.play();
  if (play && typeof play.catch === "function") play.catch(() => {});
}

function tickDualCrossfade() {
  if (engineMode !== "dual" || !engineWanted || !unlocked || dualFading) return;
  const a = dualActive;
  const b = a === dualA ? dualB : dualA;
  if (!a || !b) return;

  const dur = a.duration;
  if (!Number.isFinite(dur) || dur < 0.5) return;

  const remain = dur - a.currentTime;
  const fade = Math.min(0.28, Math.max(0.14, dur * 0.1));
  if (remain > fade) return;

  dualFading = true;
  b.volume = 0;
  try {
    b.currentTime = 0;
  } catch {
    /* ignore */
  }
  const play = b.play();
  if (play && typeof play.catch === "function") play.catch(() => {});

  const started = performance.now();
  const fadeMs = fade * 1000;
  const step = (now) => {
    if (!engineWanted) {
      dualFading = false;
      return;
    }
    const t = Math.min(1, (now - started) / fadeMs);
    const vol = engineVol;
    b.volume = vol * t;
    a.volume = vol * (1 - t);
    if (t < 1) {
      requestAnimationFrame(step);
      return;
    }
    try {
      a.pause();
      a.currentTime = 0;
      a.volume = vol;
    } catch {
      /* ignore */
    }
    dualActive = b;
    dualFading = false;
  };
  requestAnimationFrame(step);
}

function applyEngineVolume(vol) {
  engineVol = vol;
  if (engineGain && audioCtx) {
    engineGain.gain.setTargetAtTime(vol, audioCtx.currentTime, 0.05);
  }
  if (engineMode === "dual" && dualActive && !dualFading) {
    dualActive.volume = vol;
  }
}

function startCurrentEngine() {
  ensureMaster();
  if (engineMode === "buffer" && raceBuffer) {
    if (!bufferSource) startBufferLoop();
    return;
  }
  if (engineMode === "dual") {
    const active = dualActive || dualA;
    if (active && active.paused) startDualLoop();
  }
}

export async function unlockRaceAudio() {
  const ctx = getCtx();
  ensureMaster();
  if (ctx && ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      /* ignore */
    }
  }

  try {
    await preloadPromise;
  } catch {
    /* dual fallback already set */
  }

  if (engineMode === "dual" && dualA) {
    try {
      dualA.muted = true;
      dualB.muted = true;
      await dualA.play();
      dualA.pause();
      await dualB.play();
      dualB.pause();
      dualA.currentTime = 0;
      dualB.currentTime = 0;
      dualA.muted = false;
      dualB.muted = false;
    } catch {
      /* retry on start */
    }
  }

  unlocked = Boolean(ctx?.state === "running" || dualA || raceBuffer);
}

function noiseBuffer(ctx, seconds = 0.35) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  }
  return buffer;
}

/** Loop theme race_sound as the car engine (gapless). */
export function startEngineSound() {
  engineWanted = true;
  ensureMaster();
  if (!unlocked) return;

  if (engineMode === "none") {
    preloadPromise.then(() => {
      if (engineWanted && unlocked) startCurrentEngine();
    });
    return;
  }
  startCurrentEngine();
}

export function updateEngineSound({ nitro = false, muted = false } = {}) {
  ensureMaster();
  if (muted || !unlocked) {
    applyEngineVolume(0);
    return;
  }

  if (engineWanted) startCurrentEngine();
  applyEngineVolume(nitro ? 0.98 : 0.9);
  tickDualCrossfade();
}

export function stopEngineSound() {
  engineWanted = false;
  disposeBufferSource();
  if (dualA) {
    try {
      dualA.pause();
      dualA.currentTime = 0;
    } catch {
      /* ignore */
    }
  }
  if (dualB) {
    try {
      dualB.pause();
      dualB.currentTime = 0;
    } catch {
      /* ignore */
    }
  }
  dualFading = false;
  applyEngineVolume(0);
}

/** Sharp F1 barrier / carbon impact. */
export function playCrashSound() {
  const ctx = getCtx();
  const master = ensureMaster();
  if (!ctx || !master || !unlocked) return;

  const now = ctx.currentTime;
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, 0.4);
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = "bandpass";
  noiseFilter.frequency.value = 520;
  noiseFilter.Q.value = 0.9;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.7, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(master);
  noise.start(now);
  noise.stop(now + 0.42);

  const thump = ctx.createOscillator();
  thump.type = "triangle";
  thump.frequency.setValueAtTime(220, now);
  thump.frequency.exponentialRampToValueAtTime(45, now + 0.28);
  const thumpGain = ctx.createGain();
  thumpGain.gain.setValueAtTime(0.55, now);
  thumpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
  thump.connect(thumpGain);
  thumpGain.connect(master);
  thump.start(now);
  thump.stop(now + 0.3);

  const metal = ctx.createOscillator();
  metal.type = "square";
  metal.frequency.setValueAtTime(880, now);
  metal.frequency.exponentialRampToValueAtTime(180, now + 0.12);
  const metalGain = ctx.createGain();
  metalGain.gain.setValueAtTime(0.12, now);
  metalGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
  metal.connect(metalGain);
  metalGain.connect(master);
  metal.start(now);
  metal.stop(now + 0.15);
}

/** DRS / turbo-style boost whoosh. */
export function playBoostSound() {
  const ctx = getCtx();
  const master = ensureMaster();
  if (!ctx || !master || !unlocked) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(240, now);
  osc.frequency.exponentialRampToValueAtTime(980, now + 0.42);

  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 280;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.62, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(master);
  osc.start(now);
  osc.stop(now + 0.52);

  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, 0.45);
  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(0.48, now);
  nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  const nFilter = ctx.createBiquadFilter();
  nFilter.type = "highpass";
  nFilter.frequency.value = 900;
  noise.connect(nFilter);
  nFilter.connect(nGain);
  nGain.connect(master);
  noise.start(now);
  noise.stop(now + 0.48);
}

/** Lights-out style countdown beep (3-2-1) or GO chirp. */
export function playCountdownSound(isGo = false) {
  const ctx = getCtx();
  const master = ensureMaster();
  if (!ctx || !master || !unlocked) return;

  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  if (isGo) {
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.setValueAtTime(880, now + 0.08);
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.35, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
    osc.start(now);
    osc.stop(now + 0.4);
  } else {
    osc.frequency.value = 520;
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    osc.start(now);
    osc.stop(now + 0.24);
  }
  osc.connect(gain);
  gain.connect(master);
}

export function stopAllRaceSounds() {
  stopEngineSound();
}
